import { create } from "zustand";
import { persist } from "zustand/middleware";
import axios from "axios";
import apiService from "../services/apiService";
import activityMonitor from "../utils/activityMonitor";
import { getTokenExpiryTime, isTokenExpired, shouldRefreshToken } from "../utils/tokenUtils";
import JWT_CONFIG from "../config/jwtConfig";

const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      login: async (credentials) => {
        set({ isLoading: true, error: null });
        try {
          const response = await apiService.post("/auth/login", credentials);
          const { user, tokens } = response.data.data;
          set({
            user,
            tokens,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          localStorage.setItem("QQCRM-accessToken", tokens?.accessToken);
          localStorage.setItem("QQCRM-refreshToken", tokens?.refreshToken);

          // Set auth header for future requests
          apiService.setAuthToken(tokens.accessToken);

          // Start activity monitoring on login
          activityMonitor.start();
          if (JWT_CONFIG.DEBUG_MODE) {
            console.log('[AuthStore] Activity monitoring started on login');
          }

          return { success: true, user };
        } catch (error) {
          const errorMessage = error.response?.data?.message || "Login failed";
          set({
            user: null,
            tokens: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
          });
          return { success: false, error: errorMessage };
        }
      },

      register: async (userData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await apiService.post("/auth/register", userData);
          const { user, tenant } = response.data.data;

          set({
            isLoading: false,
            error: null,
          });

          return { success: true, user, tenant };
        } catch (error) {
          const errorMessage =
            error.response?.data?.message || "Registration failed";
          set({
            isLoading: false,
            error: errorMessage,
          });
          return { success: false, error: errorMessage };
        }
      },

      logout: async () => {
        const { tokens } = get();

        try {
          if (tokens?.refreshToken) {
            await apiService.post("/auth/logout", {
              refreshToken: tokens.refreshToken,
            });
          }
        } catch (error) {
          console.error("Logout error:", error);
        }

        // Stop activity monitoring
        activityMonitor.stop();
        if (JWT_CONFIG.DEBUG_MODE) {
          console.log('[AuthStore] Activity monitoring stopped');
        }

        // Clear all auth-related localStorage items
        localStorage.removeItem("auth-storage");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        localStorage.removeItem("QQCRM-accessToken");
        localStorage.removeItem("QQCRM-refreshToken");
        apiService.clearAuthToken();
        set({
          user: null,
          tokens: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      },

      refreshToken: async () => {
        const { tokens } = get();

        if (!tokens?.refreshToken) {
          get().logout();
          return false;
        }

        try {
          // Create a direct axios instance to avoid interceptor loops
          const directApi = axios.create({
            baseURL:
              process.env.REACT_APP_API_URL || "http://localhost:5002/api",
            timeout: 10000,
            headers: {
              "Content-Type": "application/json",
            },
          });

          const response = await directApi.post("/auth/refresh-token", {
            refreshToken: tokens.refreshToken,
          });

          const newTokens = response.data.data;

          set({
            tokens: newTokens,
            error: null,
          });

          // Update auth header
          apiService.setAuthToken(newTokens.accessToken);

          return true;
        } catch (error) {
          console.error("Token refresh failed:", error);
          get().logout();
          return false;
        }
      },

      updateProfile: async (profileData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await apiService.put("/auth/profile", profileData);
          const updatedUser = response.data.data;

          set({
            user: updatedUser,
            isLoading: false,
            error: null,
          });

          return { success: true, user: updatedUser };
        } catch (error) {
          const errorMessage =
            error.response?.data?.message || "Profile update failed";
          set({
            isLoading: false,
            error: errorMessage,
          });
          return { success: false, error: errorMessage };
        }
      },

      changePassword: async (passwordData) => {
        set({ isLoading: true, error: null });
        try {
          await apiService.post("/auth/change-password", passwordData);

          set({
            isLoading: false,
            error: null,
          });

          return { success: true };
        } catch (error) {
          const errorMessage =
            error.response?.data?.message || "Password change failed";
          set({
            isLoading: false,
            error: errorMessage,
          });
          return { success: false, error: errorMessage };
        }
      },

      clearError: () => set({ error: null }),

      // Initialize auth state from stored tokens
      initializeAuth: async () => {
        const { tokens } = get();

        if (tokens?.accessToken) {
          apiService.setAuthToken(tokens.accessToken);

          // Start activity monitoring
          activityMonitor.start();
          if (JWT_CONFIG.DEBUG_MODE) {
            console.log('[AuthStore] Activity monitoring started');
          }

          // Add listener for idle state changes
          activityMonitor.addListener((state) => {
            if (JWT_CONFIG.DEBUG_MODE) {
              if (state === 'idle') {
                console.log('[AuthStore] User is now idle - token refresh will pause');
              } else {
                console.log('[AuthStore] User is now active - token refresh resumed');
              }
            }
          });

          try {
            const response = await apiService.get("/auth/profile");
            const user = response.data.data;

            set({
              user,
              isAuthenticated: true,
              error: null,
            });
          } catch (error) {
            // Token might be expired, try to refresh
            const refreshSuccess = await get().refreshToken();
            if (!refreshSuccess) {
              get().logout();
            }
          }
        }
      },

      // Helper: Check if access token is expired
      isAccessTokenExpired: () => {
        const { tokens } = get();
        if (!tokens?.accessToken) return true;
        return isTokenExpired(tokens.accessToken);
      },

      // Helper: Get time remaining on access token
      getAccessTokenTimeRemaining: () => {
        const { tokens } = get();
        if (!tokens?.accessToken) return -1;
        return getTokenExpiryTime(tokens.accessToken);
      },

      // Helper: Check if token should be refreshed
      shouldRefreshAccessToken: () => {
        const { tokens } = get();
        if (!tokens?.accessToken) return false;
        return shouldRefreshToken(tokens.accessToken, 5 * 60 * 1000); // 5 minutes
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        user: state.user,
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

export default useAuthStore;
